import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import StaffNavbar from '../components/StaffNavbar';
import StaffFansStatsCard from '../components/StaffFansStatsCard';
import Icon from '../components/Icon';
import apiService from '../services/api';
import '../styles/StaffPages.css';

const StaffAuctions = () => {
  const navigate = useNavigate();
  const [auctions, setAuctions] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [sortConfig, setSortConfig] = useState({
    key: null,
    direction: 'asc'
  });

  useEffect(() => {
    fetchAuctions();
  }, []);

  const fetchAuctions = async () => {
    try {
      setLoading(true);
      const rewardsData = await apiService.getRewards();
      const rewards = rewardsData.results || rewardsData || [];
      const auctionRewards = rewards.filter(reward => reward.redemption_type === 'auction');
      setAuctions(auctionRewards);
      setError(null);
    } catch (err) {
      console.error('Error fetching auctions:', err);
      setError('Failed to load auctions data');
    } finally {
      setLoading(false);
    }
  };

  const handleSort = (key) => {
    let direction = 'asc';
    if (sortConfig.key === key && sortConfig.direction === 'asc') {
      direction = 'desc';
    }
    setSortConfig({ key, direction });
  };

  const sortedAuctions = React.useMemo(() => {
    let sortableAuctions = [...auctions];
    if (sortConfig.key !== null) {
      sortableAuctions.sort((a, b) => {
        let aValue, bValue;

        switch (sortConfig.key) {
          case 'title':
            aValue = a.title;
            bValue = b.title;
            break;
          case 'startingBid':
            aValue = a.auction_info?.starting_bid || 0;
            bValue = b.auction_info?.starting_bid || 0;
            break;
          case 'currentBid':
            aValue = a.auction_info?.current_highest_bid || a.auction_info?.starting_bid || 0;
            bValue = b.auction_info?.current_highest_bid || b.auction_info?.starting_bid || 0;
            break;
          case 'endDate':
            aValue = a.auction_info?.end_date;
            bValue = b.auction_info?.end_date;
            break;
          case 'status':
            aValue = a.auction_info?.status || 'draft';
            bValue = b.auction_info?.status || 'draft';
            break;
          case 'bids':
            aValue = a.auction_info?.total_bids || 0;
            bValue = b.auction_info?.total_bids || 0;
            break;
          default:
            return 0;
        }

        if (aValue < bValue) {
          return sortConfig.direction === 'asc' ? -1 : 1;
        }
        if (aValue > bValue) {
          return sortConfig.direction === 'asc' ? 1 : -1;
        }
        return 0;
      });
    }
    return sortableAuctions;
  }, [auctions, sortConfig]);

  const formatDate = (dateString) => {
    if (!dateString) return 'Date TBD';
    try {
      const date = new Date(dateString);
      return date.toLocaleDateString('en-US', {
        month: 'short',
        day: 'numeric',
        hour: 'numeric',
        minute: '2-digit',
        hour12: true
      });
    } catch (error) {
      return 'Date TBD';
    }
  };

  const getStatusText = (status) => {
    const colors = {
      active: '#2196F3',
      completed: '#4CAF50',
      cancelled: '#F44336',
      draft: '#FF9800'
    };

    return (
      <span style={{
        color: colors[status] || colors.draft,
        fontSize: '14px',
        fontWeight: '500',
        textTransform: 'capitalize',
        fontFamily: '"Baikal Book"'
      }}>
        {status}
      </span>
    );
  };

  if (loading) {
    return (
      <div className="admin-dashboard">
        <StaffNavbar activeSection="auctions" />
        <div className="admin-main">
          <div className="admin-content">
            <div style={{ textAlign: 'center', padding: '2rem' }}>
              <div>Loading auctions data...</div>
            </div>
          </div>
        </div>
      </div>
    );
  }

  if (error) {
    return (
      <div className="admin-dashboard">
        <StaffNavbar activeSection="auctions" />
        <div className="admin-main">
          <div className="admin-content">
            <div style={{ textAlign: 'center', padding: '2rem', color: 'red' }}>
              <div>{error}</div>
            </div>
          </div>
        </div>
      </div>
    );
  }

  return (
    <div className="admin-dashboard">
      <StaffNavbar activeSection="auctions" />
      <div className="admin-main">
        <div className="admin-content">
          <h1 className="staff-page-header">Auction Management</h1>
          <p className="staff-page-subheader">Manage and monitor your school's auction rewards and their bidding status.</p>

          <div style={{
            display: 'flex',
            gap: '8px',
            marginTop: '24px',
            flexWrap: 'wrap'
          }}>
            <div style={{
              flex: '1 1 200px',
              minWidth: '200px',
              maxWidth: '25%'
            }}>
              <StaffFansStatsCard
                title="Active Auctions"
                value={loading ? "..." : auctions.filter(auction => auction.is_active && auction.auction_info?.status === 'active').length}
                iconName="hammer"
              />
            </div>
            <div style={{
              flex: '1 1 200px',
              minWidth: '200px',
              maxWidth: '25%'
            }}>
              <StaffFansStatsCard
                title="Completed Auctions"
                value={loading ? "..." : auctions.filter(auction => auction.auction_info?.status === 'completed').length}
                iconName="checkmark"
              />
            </div>
          </div>

          <h3 style={{
            fontSize: '16px',
            fontStyle: 'normal',
            fontWeight: '700',
            lineHeight: 'normal',
            marginTop: '32px',
            marginBottom: '4px',
          }}>
            All Auctions
          </h3>
          <p style={{
            color: 'var(--Black, #000)',
            fontSize: '12px',
            fontStyle: 'normal',
            fontWeight: '340',
            lineHeight: 'normal',
            opacity: '0.5',
            marginTop: '0',
            marginBottom: '16px'
          }}>
            A comprehensive list of all auction rewards and their current bidding status
          </p>

          <div style={{
            display: 'flex',
            padding: '18px',
            alignItems: 'center',
            alignSelf: 'stretch',
            borderRadius: '12px 12px 0 0',
            background: '#F8F8F8',
            backdropFilter: 'blur(22.5px)'
          }}>
            <div
              onClick={() => handleSort('title')}
              style={{
                fontFamily: '"Baikal Book"',
                fontWeight: '900',
                fontSize: '14px',
                lineHeight: '100%',
                letterSpacing: '0%',
                width: '25%',
                display: 'flex',
                alignItems: 'center',
                gap: '4px',
                cursor: 'pointer'
              }}>
              Title
              <Icon name="up-down" size={12} />
            </div>
            <div
              onClick={() => handleSort('currentBid')}
              style={{
                fontFamily: '"Baikal Book"',
                fontWeight: '900',
                fontSize: '14px',
                lineHeight: '100%',
                letterSpacing: '0%',
                width: '15%',
                display: 'flex',
                alignItems: 'center',
                gap: '4px',
                cursor: 'pointer'
              }}>
              Current Bid
              <Icon name="up-down" size={12} />
            </div>
            <div
              onClick={() => handleSort('startingBid')}
              style={{
                fontFamily: '"Baikal Book"',
                fontWeight: '900',
                fontSize: '14px',
                lineHeight: '100%',
                letterSpacing: '0%',
                width: '15%',
                display: 'flex',
                alignItems: 'center',
                gap: '4px',
                cursor: 'pointer'
              }}>
              Starting Bid
              <Icon name="up-down" size={12} />
            </div>
            <div
              onClick={() => handleSort('bids')}
              style={{
                fontFamily: '"Baikal Book"',
                fontWeight: '900',
                fontSize: '14px',
                lineHeight: '100%',
                letterSpacing: '0%',
                width: '10%',
                display: 'flex',
                alignItems: 'center',
                gap: '4px',
                cursor: 'pointer'
              }}>
              Bids
              <Icon name="up-down" size={12} />
            </div>
            <div
              onClick={() => handleSort('endDate')}
              style={{
                fontFamily: '"Baikal Book"',
                fontWeight: '900',
                fontSize: '14px',
                lineHeight: '100%',
                letterSpacing: '0%',
                width: '15%',
                display: 'flex',
                alignItems: 'center',
                gap: '4px',
                cursor: 'pointer'
              }}>
              End Time
              <Icon name="up-down" size={12} />
            </div>
            <div
              onClick={() => handleSort('status')}
              style={{
                fontFamily: '"Baikal Book"',
                fontWeight: '900',
                fontSize: '14px',
                lineHeight: '100%',
                letterSpacing: '0%',
                width: '10%',
                display: 'flex',
                alignItems: 'center',
                gap: '4px',
                cursor: 'pointer'
              }}>
              Status
              <Icon name="up-down" size={12} />
            </div>
            <div style={{
              fontFamily: '"Baikal Book"',
              fontWeight: '900',
              fontSize: '14px',
              lineHeight: '100%',
              letterSpacing: '0%',
              width: '10%',
              display: 'flex',
              alignItems: 'center',
              gap: '4px'
            }}>
              Action
            </div>
          </div>

          <div style={{ marginBottom: '8px' }}></div>

          {sortedAuctions.length === 0 ? (
            <div style={{
              display: 'flex',
              width: '100%',
              maxWidth: '1270px',
              height: '59px',
              padding: '18px',
              alignItems: 'center',
              borderRadius: '12px',
              background: '#F8F8F8',
              marginBottom: '8px',
              justifyContent: 'center',
              fontFamily: '"Baikal Book"',
              fontSize: '14px',
              color: '#6b7280'
            }}>
              No auctions found. Create your first auction reward to get started.
            </div>
          ) : (
            sortedAuctions.map((auction, index) => (
              <div key={auction.id || index} style={{
                display: 'flex',
                width: '100%',
                maxWidth: '1270px',
                height: '59px',
                padding: '18px',
                alignItems: 'center',
                borderRadius: '12px',
                background: '#F8F8F8',
                marginBottom: '8px'
              }}>
                <div style={{
                  fontFamily: '"Baikal Book"',
                  fontSize: '14px',
                  width: '25%',
                  display: 'flex',
                  alignItems: 'center',
                  fontWeight: 'bold'
                }}>
                  {auction.title}
                </div>
                <div style={{
                  fontFamily: '"Baikal Book"',
                  fontSize: '14px',
                  width: '15%',
                  display: 'flex',
                  alignItems: 'center'
                }}>
                  {(auction.auction_info?.current_highest_bid || auction.auction_info?.starting_bid || 0).toLocaleString()} pts
                </div>
                <div style={{
                  fontFamily: '"Baikal Book"',
                  fontSize: '14px',
                  width: '15%',
                  display: 'flex',
                  alignItems: 'center'
                }}>
                  {(auction.auction_info?.starting_bid || 0).toLocaleString()} pts
                </div>
                <div style={{
                  fontFamily: '"Baikal Book"',
                  fontSize: '14px',
                  width: '10%',
                  display: 'flex',
                  alignItems: 'center'
                }}>
                  {(auction.auction_info?.total_bids || 0).toLocaleString()}
                </div>
                <div style={{
                  fontFamily: '"Baikal Book"',
                  fontSize: '14px',
                  width: '15%',
                  display: 'flex',
                  alignItems: 'center'
                }}>
                  {formatDate(auction.auction_info?.end_date)}
                </div>
                <div style={{
                  width: '10%',
                  display: 'flex',
                  alignItems: 'center'
                }}>
                  {getStatusText(auction.auction_info?.status || 'draft')}
                </div>
                <div style={{
                  width: '10%',
                  display: 'flex',
                  alignItems: 'center'
                }}>
                  <button
                    className="staff-page-button"
                    style={{
                      display: 'flex',
                      alignItems: 'center',
                      gap: '6px',
                      padding: '8px 12px',
                      fontSize: '12px',
                      fontWeight: '340',
                      transition: 'all 0.2s ease',
                      backgroundColor: auction.auction_info?.status === 'completed' ? 'transparent' : '#00C8FF',
                      color: auction.auction_info?.status === 'completed' ? '#00C8FF' : '#000',
                      border: auction.auction_info?.status === 'completed' ? '1px solid #00C8FF' : 'none'
                    }}
                    onMouseEnter={(e) => {
                      if (auction.auction_info?.status === 'completed') {
                        e.target.style.backgroundColor = '#00C8FF';
                        e.target.style.color = '#000';
                      } else {
                        e.target.style.backgroundColor = '#00B0E6';
                      }
                    }}
                    onMouseLeave={(e) => {
                      if (auction.auction_info?.status === 'completed') {
                        e.target.style.backgroundColor = 'transparent';
                        e.target.style.color = '#00C8FF';
                      } else {
                        e.target.style.backgroundColor = '#00C8FF';
                      }
                    }}
                    onClick={() => navigate(`/staff/auctions/${auction.auction_info?.id || auction.id}/details/`)}
                  >
                    Details
                  </button>
                </div>
              </div>
            ))
          )}
        </div>
      </div>
    </div>
  );
};

export default StaffAuctions;